import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { Seo } from "../components/Seo";
import { supabase } from "../lib/supabase";
import type { Dorama } from "../types";

type Purchase = {
  id: string;
  purchased_at: string;
  amount_paid: number;
  dorama: {
    id: string;
    slug: string | null;
    title: string;
    cover_url: string | null;
    year: number | null;
    category: string | null;
  } | null;
};

type Favorite = { dorama: Dorama | null };
type LifetimeAccess = { status: string; expires_at: string | null };

export function MyPurchasesPage() {
  const { user, loading: authLoading } = useAuth();
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [favorites, setFavorites] = useState<Dorama[]>([]);
  const [lifetimeAccess, setLifetimeAccess] = useState<LifetimeAccess | null>(null);
  const [activeTab, setActiveTab] = useState<"purchases" | "favorites">("purchases");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    let active = true;
    setLoading(true);
    Promise.all([
      supabase.from("purchases").select(`id, purchased_at, amount_paid, dorama:doramas (id, slug, title, cover_url, year, category)`).eq("user_id", user.id).eq("status", "confirmed").order("purchased_at", { ascending: false }),
      supabase.from("favorites").select("dorama:doramas (*)").eq("user_id", user.id).order("created_at", { ascending: false }),
      supabase.from("lifetime_access").select("status, expires_at").eq("user_id", user.id).in("status", ["active", "trial"]).maybeSingle(),
    ]).then(([purchaseResult, favoriteResult, lifetimeResult]) => {
      if (!active) return;
      if (purchaseResult.error || favoriteResult.error || lifetimeResult.error) setError("Não foi possível carregar sua biblioteca.");
      else {
        setPurchases((purchaseResult.data || []) as unknown as Purchase[]);
        setFavorites(((favoriteResult.data || []) as unknown as Favorite[]).flatMap((item) => item.dorama ? [item.dorama] : []));
        setLifetimeAccess(lifetimeResult.data as LifetimeAccess | null);
      }
      setLoading(false);
    });
    return () => { active = false; };
  }, [user]);

  return (
    <section className="purchases-page page-container">
      <Seo title="Minhas Compras | DoramaFlix" description="Acesse as novelinhas compradas na sua conta DoramaFlix." path="/minhas-compras" />
      <header className="article-header"><p className="eyebrow">SUA BIBLIOTECA</p><h1>Minhas Compras</h1><p>Os títulos liberados ficam vinculados à sua conta.</p></header>
      {authLoading && <div className="status-box" role="status">Verificando sua sessão…</div>}
      {!authLoading && !user && <div className="empty-library"><h2>Entre para ver suas compras</h2><p>Use a mesma conta informada no pagamento.</p><Link className="primary-button" to="/?entrar=1">Entrar na minha conta</Link></div>}
      {user && loading && <div className="status-box" role="status">Carregando suas compras…</div>}
      {user && error && <div className="status-box error" role="alert">{error}</div>}
      {user && !loading && !error && <>
        {lifetimeAccess && <div className="library-vip-banner"><span>♛</span><div><strong>Passe Vitalício ativo</strong><small>{lifetimeAccess.status === "trial" ? "Acesso de demonstração liberado para todo o catálogo." : "Todo o catálogo está liberado na sua conta para sempre."}</small></div><Link to="/">Ver catálogo →</Link></div>}
        <div className="library-tabs" role="tablist" aria-label="Sua biblioteca">
          <button type="button" role="tab" aria-selected={activeTab === "purchases"} className={activeTab === "purchases" ? "active" : ""} onClick={() => setActiveTab("purchases")}>Compras <span>{purchases.length}</span></button>
          <button type="button" role="tab" aria-selected={activeTab === "favorites"} className={activeTab === "favorites" ? "active" : ""} onClick={() => setActiveTab("favorites")}>Favoritos <span>{favorites.length}</span></button>
        </div>
        {activeTab === "purchases" && <>
          {purchases.length === 0 && <div className="empty-library"><span className="empty-library-icon">▱</span><h2>Nenhuma compra encontrada</h2><p>Quando você desbloquear uma novelinha, ela aparecerá aqui.</p><Link className="primary-button" to="/">Explorar catálogo</Link></div>}
          <div className="purchase-grid">{purchases.map((purchase) => purchase.dorama && <article key={purchase.id}><Link to={`/dorama/${purchase.dorama.slug || purchase.dorama.id}`}>{purchase.dorama.cover_url ? <img src={purchase.dorama.cover_url} alt={`Capa de ${purchase.dorama.title}`} /> : <span className="purchase-placeholder">Sem capa</span>}<div><p>{purchase.dorama.category || "Dorama"}</p><h2>{purchase.dorama.title}</h2><span>Comprado em {new Date(purchase.purchased_at).toLocaleDateString("pt-BR")}</span><strong>Assistir agora →</strong></div></Link></article>)}</div>
        </>}
        {activeTab === "favorites" && <>
          {favorites.length === 0 && <div className="empty-library"><span className="empty-library-icon">♡</span><h2>Nenhum favorito ainda</h2><p>Use o coração nas páginas dos títulos para montar sua lista.</p><Link className="primary-button" to="/">Descobrir novelinhas</Link></div>}
          <div className="favorite-library-grid">{favorites.map((dorama) => <article key={dorama.id}><Link to={`/dorama/${dorama.slug || dorama.id}`}><div>{dorama.cover_url ? <img src={dorama.cover_url} alt={`Capa de ${dorama.title}`} loading="lazy" /> : <span>Sem capa</span>}<i aria-hidden="true">♥</i></div><h2>{dorama.title}</h2><p>{dorama.category || "Dorama"} · {dorama.year || "Novo"}</p></Link></article>)}</div>
        </>}
      </>}
    </section>
  );
}
